import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Where Save and Cancel go from a standalone edge-management editor.
 *
 * Going back (`navigate(-1)`) is right only when the previous history entry is
 * part of this app. These editors are usually opened by URL, and nothing in the
 * app links to some of them (bank/edit, lift/edit). In that case the entry
 * before them belongs to another document, often the login page, so going back
 * left the app: saving a bank opened by URL ended on /login and looked like a
 * logout. In a fresh tab there is no previous entry, so it did nothing.
 *
 * The router gives the first location of a page load the key `"default"`. That
 * is how a direct load is recognised; the editor then goes to its list page
 * and replaces its own history entry, so Back does not reopen a saved form.
 *
 * @param listPath the absolute in-app path of the editor's list page.
 */
export default function useLeaveStandaloneEditor(listPath: string): () => void {
  const navigate = useNavigate();
  const { key } = useLocation();
  return useCallback(() => {
    if (key === "default") {
      navigate(listPath, { replace: true });
    } else {
      navigate(-1);
    }
  }, [key, listPath, navigate]);
}
